import { addRxPlugin, createRxDatabase } from 'rxdb/plugins/core';
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie';
import { RxDBLeaderElectionPlugin } from 'rxdb/plugins/leader-election';
import {
	adoptedSchema,
	allEndUserSchema,
	allEndUserRBDMSchema,
	allRigidUserSchema,
	clubDropdownSchema,
	RbdmClubListSchema,
	competitionBrandSchema,
	dealerSchema,
	gallerySchema,
	ImrCodeSchema,
	leadUserSchema,
	lmsUserLeadSchema,
	ProductsListSchema,
	meetingsSchema,
	siteSummaryListSchema,
	userCodeSchema,
	profileSchema,
	VideoGallerySchema,
	meetingTypeSchema,
	dealerVisitsSchema,
	dealerVisitProductSchema,
	productDescriptionListSchema,
	dashboardBannerSchema,
	oneToOneMeetinsListSchema,
	endUserListSchema,
	wssTerritoryListSchema,
	dealersListSchema,
	endUserCategoryListSchema,
	FactoryVisitSchema,
	SupervisorBankingHistorySchema,
	SupervisorSchema,
	SiteSpecialistSchema,
	mdiCodeDropdownSchema,
	SupervisorRBDMSchema,
	meetingsRBDMSchema,
	siteListSchema,
	LmsSSSchema,
	siteVisitRecordingSessionSchema,
	mdpMeetingSchema,
} from '@rxdb/schema';
import { RxDBQueryBuilderPlugin } from 'rxdb/plugins/query-builder';
import { RxDBDevModePlugin } from 'rxdb/plugins/dev-mode';
import { RxDBCleanupPlugin } from 'rxdb/plugins/cleanup';
import { RxDBMigrationPlugin } from 'rxdb/plugins/migration-schema';
import { TownListSchema } from '@rxdb/schema/townListSchema';
import { subContractorsSchema } from '@rxdb/schema/subContractorUsersSchema';

addRxPlugin(RxDBMigrationPlugin);
addRxPlugin(RxDBCleanupPlugin);
if (process.env.NODE_ENV !== 'production') {
	addRxPlugin(RxDBDevModePlugin);
}
addRxPlugin(RxDBQueryBuilderPlugin);
addRxPlugin(RxDBLeaderElectionPlugin);

let dbPromise = null;
let dbInstance = null;
let myDatabase = null;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createDatabase = async () => {
	myDatabase = await createRxDatabase({
		name: 'mpowerwwa',
		storage: getRxStorageDexie(),
		multiInstance: true,
		eventReduce: true,
		cleanupPolicy: {
			minimumDeletedTime: 1000 * 60 * 60,
			minimumCollectionAge: 1000 * 60 * 60,
			runEach: 5 * 60 * 1000,
		},
	});

	window.db = myDatabase;

	await myDatabase.addCollections({
		dashboard_banner: {
			schema: dashboardBannerSchema,
		},
		adopted_contractor: adoptedSchema,
		new_leads: {
			schema: leadUserSchema,
			migrationStrategies: {
				1: async (oldDoc) => {
					return {
						id: oldDoc.id.toString(),
						...oldDoc,
					};
				},
				2: async (oldDoc) => {
					return {
						...oldDoc,
					};
				},
			},
		},
		all: allEndUserSchema,
		all_rbdm: allEndUserRBDMSchema,
		all_rigid: allRigidUserSchema,
		sub_contractor_list: subContractorsSchema,
		club: {
			schema: clubDropdownSchema,
			migrationStrategies: {
				1: async (oldDoc) => {
					return {
						...oldDoc,
					};
				},
			},
		},
		rbdm_club: {
			schema: RbdmClubListSchema,
			migrationStrategies: {
				1: async (oldDoc) => {
					return {
						...oldDoc,
					};
				},
			},
		},
		dealer: {
			schema: dealerSchema,
		},
		competition_brand: {
			schema: competitionBrandSchema,
		},
		gallery: {
			schema: gallerySchema,
		},
		user_list: {
			schema: userCodeSchema,
		},
		site_summary_list: {
			schema: siteSummaryListSchema,
			migrationStrategies: {
				1: async (oldDoc) => {
					return {
						...oldDoc,
					};
				},
				2: async (oldDoc) => {
					return {
						...oldDoc,
					};
				},
				3: async (oldDoc) => {
					return {
						...oldDoc,
					};
				},
				4: async (oldDoc) => {
					return {
						...oldDoc,
					};
				},
				5: async (oldDoc) => {
					return {
						...oldDoc,
					};
				},
				6: async (oldDoc) => {
					return {
						...oldDoc,
						lead_contractor_phone: null, // Add the new field with default value
					};
				},
				7: async (oldDoc) => {
					return {
						...oldDoc,
						primary_cmdi: oldDoc.primary_cmdi || null, // Add the new field with default value
					};
				},
				8: async (oldDoc) => {
					return {
						...oldDoc,
						site_current_status_c: oldDoc.site_current_status_c || null, // Add the new field with default value
					};
				},
				9: async (oldDoc) => {
					return {
						...oldDoc,
						end_user_source: oldDoc.end_user_source || null,
					};
				},
				10: async (oldDoc) => {
					return {
						...oldDoc,
						cmdi_code_c: oldDoc.cmdi_code_c || null,
					};
				},
				11: async (oldDoc) => {
					return {
						...oldDoc,
						source_of_site_lead_c: oldDoc.source_of_site_lead_c || null,
					};
				},
			},
		},
		imr_code_list: {
			schema: ImrCodeSchema,
		},
		towns: {
			schema: TownListSchema,
		},
		products: {
			schema: ProductsListSchema,
		},
		meeting_list: {
			schema: meetingsSchema,
			migrationStrategies: {
				1: async (oldDoc) => {
					return {
						id: oldDoc.id.toString(),
						...oldDoc,
					};
				},
				2: async (oldDoc) => {
					return {
						...oldDoc,
					};
				},
			},
		},
		meeting_list_rbdm: {
			schema: meetingsRBDMSchema,
		},
		profile: {
			schema: profileSchema,
			migrationStrategies: {
				1: async (oldDoc) => {
					return {
						...oldDoc,
					};
				},
				2: async (oldDoc) => {
					return {
						...oldDoc,
					};
				},
				3: async (oldDoc) => {
					return {
						...oldDoc,
					};
				},
			},
		},
		video_gallery: {
			schema: VideoGallerySchema,
		},
		meeting_types: meetingTypeSchema,
		dealer_visits: dealerVisitsSchema,
		dealer_visit_products: dealerVisitProductSchema,
		product_description: productDescriptionListSchema,
		one_to_one_meetings: oneToOneMeetinsListSchema,
		end_user_list: endUserListSchema,
		wss_territory_list: wssTerritoryListSchema,
		dealers_list: dealersListSchema,
		enduser_category_list: endUserCategoryListSchema,
		supervisor_list: SupervisorSchema,
		supervisor_rbdm_list: SupervisorRBDMSchema,
		factory_visit_list: FactoryVisitSchema,
		supervisor_banking_history: SupervisorBankingHistorySchema,
		sitespecialist_list: SiteSpecialistSchema,
		other_products: {
			schema: ProductsListSchema,
		},
		mdi_code_list: {
			schema: mdiCodeDropdownSchema,
			migrationStrategies: {
				1: async (oldDoc) => {
					return {
						...oldDoc,
					};
				},
			},
		},
		lms_site_list: {
			schema: siteListSchema,
		},
		lms_user_leads: {
			schema: lmsUserLeadSchema,
			migrationStrategies: {
				1: async (oldDoc) => {
					return {
						...oldDoc,
						glue_consumption_per_month: oldDoc.glue_consumption_per_month ?? null,
					};
				},
			},
		},
		lms_ss_list: LmsSSSchema,
		mdp_meeting_list: {
			schema: mdpMeetingSchema,
		},
		site_visit_recording_sessions: {
			schema: siteVisitRecordingSessionSchema,
		},
	});

	dbInstance = myDatabase;
	return myDatabase;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const getDatabase = () => {
	if (!dbPromise) dbPromise = createDatabase();
	return dbPromise;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const cleanupDatabase = async () => {
	if (dbInstance) {
		dbInstance.broadcastChannel?.close();
		const collections = Object.values(dbInstance.collections);

		// Remove all data from collections
		await Promise.all(
			collections.map(async (collection) => {
				await collection.remove();
			})
		);

		// Destroy the database
		await dbInstance.destroy();

		dbInstance = null;
		dbPromise = null;
	}
};
